import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Inbox, ArrowLeft, CalendarPlus, Trash2, ImagePlus, Undo2, StickyNote, Quote, Eye, EyeOff, Pencil } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mahima-admin-token";
const EVENT_TYPES = ["Retreat", "Satsang", "Workshop", "Online"];

const inputStyle = {
  backgroundColor: "var(--bg-canvas)",
  borderColor: "var(--border-delicate)",
  color: "var(--text-primary)",
};

const EMPTY_EVENT = { title: "", eventType: "Satsang", date: "", endDate: "", location: "", description: "", link: "" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [galleryCustom, setGalleryCustom] = useState([]);
  const [galleryVer, setGalleryVer] = useState(0);
  const [uploading, setUploading] = useState(0);
  const [notes, setNotes] = useState({});
  const fileInputs = useRef({});

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const loadEvents = () =>
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});

  const loadGallery = () =>
    axios.get(`${API}/gallery`).then((r) => setGalleryCustom(r.data.positions || [])).catch(() => {});

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/inquiries`, { headers: authHeaders() })
      .then((r) => {
        setInquiries(r.data);
        const n = {};
        r.data.forEach((q) => { n[q.id] = q.note || ""; });
        setNotes(n);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
    loadEvents();
    loadGallery();
    loadTestimonials();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setInquiries(null);
  };

  const setEv = (k) => (e) => setEventForm((f) => ({ ...f, [k]: e.target.value }));

  const addEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/events`, eventForm, { headers: authHeaders() });
      toast.success("Event added");
      setEventForm(EMPTY_EVENT);
      loadEvents();
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Session expired — please log in again" : "Could not add event");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { headers: authHeaders() });
      setEvents((ev) => ev.filter((x) => x.id !== id));
      toast.success("Event removed");
    } catch (err) {
      toast.error("Could not remove event");
    }
  };

  const EMPTY_TESTIMONIAL = { text: "", name: "", role: "", published: true };
  const [testimonials, setTestimonials] = useState([]);
  const [tForm, setTForm] = useState(EMPTY_TESTIMONIAL);
  const [tEditing, setTEditing] = useState(null);
  const [tSaving, setTSaving] = useState(false);

  const loadTestimonials = () =>
    axios.get(`${API}/testimonials/all`, { headers: authHeaders() }).then((r) => setTestimonials(r.data)).catch(() => {});

  const setT = (k) => (e) => setTForm((f) => ({ ...f, [k]: e.target.value }));

  const saveTestimonial = async (e) => {
    e.preventDefault();
    setTSaving(true);
    try {
      if (tEditing) {
        await axios.put(`${API}/testimonials/${tEditing}`, tForm, { headers: authHeaders() });
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${API}/testimonials`, tForm, { headers: authHeaders() });
        toast.success("Testimonial added");
      }
      setTForm(EMPTY_TESTIMONIAL);
      setTEditing(null);
      loadTestimonials();
    } catch (err) {
      toast.error("Could not save testimonial");
    } finally {
      setTSaving(false);
    }
  };

  const togglePublish = async (tm) => {
    try {
      await axios.put(`${API}/testimonials/${tm.id}`, { text: tm.text, name: tm.name, role: tm.role, published: !tm.published }, { headers: authHeaders() });
      toast.success(tm.published ? "Unpublished — hidden from site" : "Published — live on site");
      loadTestimonials();
    } catch (err) {
      toast.error("Could not update");
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: authHeaders() });
      setTestimonials((ts) => ts.filter((x) => x.id !== id));
      toast.success("Testimonial deleted");
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const uploadPhoto = async (position, file) => {
    if (!file) return;
    setUploading(position);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/gallery/${position}`, fd, { headers: authHeaders() });
      toast.success(`Photo ${position} updated`);
      setGalleryVer((v) => v + 1);
      loadGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(0);
    }
  };

  const resetPhoto = async (position) => {
    try {
      await axios.delete(`${API}/gallery/${position}`, { headers: authHeaders() });
      toast.success(`Photo ${position} back to default`);
      setGalleryVer((v) => v + 1);
      loadGallery();
    } catch (err) {
      toast.error("Could not reset photo");
    }
  };

  const saveNote = async (id) => {
    try {
      const { data } = await axios.put(`${API}/inquiries/${id}/note`, { note: notes[id] || "" }, { headers: authHeaders() });
      setInquiries((prev) =>
        (prev || []).map((x) =>
          x.id === id
            ? { ...x, note: notes[id] || "", noteLogs: [...(x.noteLogs || []), { note: notes[id] || "", savedAt: data.savedAt }] }
            : x
        )
      );
      toast.success("Note saved");
    } catch (err) {
      toast.error("Could not save note");
    }
  };

  return (
    <div className="themed min-h-screen px-5 py-24 sm:px-8" style={{ backgroundColor: "var(--bg-canvas)", color: "var(--text-primary)" }}>
      <div className="mx-auto max-w-4xl">
        <Link to="/" data-testid="admin-back-link" className="mb-10 inline-flex items-center gap-2 text-xs tracking-wide" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft size={14} /> Back to site
        </Link>

        {!token ? (
          <form
            onSubmit={login}
            className="mx-auto mt-8 max-w-md rounded-3xl border p-8"
            style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}
          >
            <div className="mb-6 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                <Lock size={17} />
              </span>
              <div>
                <h1 className="font-display text-2xl">Private Inbox</h1>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>For Maa Prem Mahima's eyes only</p>
              </div>
            </div>
            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>Email</label>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mb-4 w-full rounded-xl border px-4 py-3 text-sm outline-none" style={inputStyle} placeholder="admin email" />
            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>Password</label>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mb-5 w-full rounded-xl border px-4 py-3 text-sm outline-none" style={inputStyle} placeholder="••••••••" />
            {error && (
              <p data-testid="admin-login-error" className="mb-4 text-sm" style={{ color: "#e0607e" }}>{error}</p>
            )}
            <button data-testid="admin-login-button" type="submit" disabled={loading}
              className="w-full rounded-full px-8 py-3.5 text-sm font-semibold tracking-wide disabled:opacity-60"
              style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}>
              {loading ? "Opening…" : "Open Inbox"}
            </button>
          </form>
        ) : (
          <div>
            <div className="mb-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                  <Inbox size={17} />
                </span>
                <div>
                  <h1 className="font-display text-2xl sm:text-3xl">Sacred Inquiries</h1>
                  <p data-testid="admin-inquiries-count" className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {inquiries ? `${inquiries.length} received` : "Loading…"}
                  </p>
                </div>
              </div>
              <button data-testid="admin-logout-button" onClick={logout}
                className="flex items-center gap-2 rounded-full border px-4 py-2 text-xs"
                style={{ borderColor: "var(--border-delicate)", color: "var(--text-secondary)" }}>
                <LogOut size={13} /> Logout
              </button>
            </div>

            <section className="mb-10 rounded-3xl border p-6 sm:p-8" style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}>
              <h2 className="font-display flex items-center gap-2 text-xl sm:text-2xl">
                <ImagePlus size={18} style={{ color: "var(--accent-gold)" }} /> Gallery Photos
              </h2>
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                Replace any of the 9 frames in "The path, in frames". Uploads appear on the site instantly.
              </p>
              <div data-testid="admin-gallery-grid" className="mt-6 grid grid-cols-3 gap-4">
                {Array.from({ length: 9 }, (_, i) => i + 1).map((pos) => {
                  const isCustom = galleryCustom.includes(pos);
                  const src = isCustom
                    ? `${API}/gallery/image/${pos}?v=${galleryVer}`
                    : `/images/gallery-${pos}.jpg`;
                  return (
                    <div key={pos} data-testid={`admin-gallery-slot-${pos}`} className="relative">
                      <img src={src} alt={`Gallery slot ${pos}`}
                        className="aspect-square w-full rounded-xl border object-cover object-top"
                        style={{ borderColor: "var(--border-delicate)" }} />
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <button
                          data-testid={`gallery-upload-${pos}`}
                          onClick={() => fileInputs.current[pos]?.click()}
                          disabled={uploading === pos}
                          className="flex-1 rounded-full border px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] disabled:opacity-50"
                          style={{ borderColor: "var(--border-hover)", color: "var(--text-secondary)" }}
                        >
                          {uploading === pos ? "Uploading…" : isCustom ? "Replace" : "Change"}
                        </button>
                        {isCustom && (
                          <button
                            data-testid={`gallery-reset-${pos}`}
                            onClick={() => resetPhoto(pos)}
                            aria-label={`Reset photo ${pos}`}
                            className="rounded-full border p-1.5"
                            style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}
                          >
                            <Undo2 size={12} />
                          </button>
                        )}
                      </div>
                      <input
                        ref={(el) => (fileInputs.current[pos] = el)}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => { uploadPhoto(pos, e.target.files?.[0]); e.target.value = ""; }}
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="mb-10 rounded-3xl border p-6 sm:p-8" style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}>
              <h2 className="font-display flex items-center gap-2 text-xl sm:text-2xl">
                <Quote size={18} style={{ color: "var(--accent-gold)" }} /> Testimonials
              </h2>
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                Only published testimonials appear on the site. Unpublish to hide without deleting.
              </p>

              <form onSubmit={saveTestimonial} className="mt-6 grid gap-4">
                <textarea data-testid="testimonial-input-text" required rows={3} value={tForm.text} onChange={setT("text")}
                  className="resize-none rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle}
                  placeholder="The participant's words…" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="testimonial-input-name" required value={tForm.name} onChange={setT("name")}
                    className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Name (e.g. A. Malik)" />
                  <input data-testid="testimonial-input-role" value={tForm.role} onChange={setT("role")}
                    className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Role (e.g. Retreat Participant)" />
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid="testimonial-save-button" type="submit" disabled={tSaving}
                    className="rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-60"
                    style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}>
                    {tSaving ? "Saving…" : tEditing ? "Update Testimonial" : "Add Testimonial"}
                  </button>
                  {tEditing && (
                    <button type="button" data-testid="testimonial-cancel-edit"
                      onClick={() => { setTEditing(null); setTForm(EMPTY_TESTIMONIAL); }}
                      className="rounded-full border px-5 py-2.5 text-xs"
                      style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}>
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>

              <div data-testid="admin-testimonials-list" className="mt-6 space-y-3">
                {testimonials.map((tm) => (
                  <div key={tm.id} data-testid={`admin-testimonial-${tm.id}`}
                    className="rounded-2xl border p-5"
                    style={{
                      borderColor: "var(--border-delicate)",
                      backgroundColor: "var(--bg-canvas)",
                      opacity: tm.published ? 1 : 0.55,
                    }}>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>"{tm.text}"</p>
                    <p className="mt-2 text-xs" style={{ color: "var(--accent-gold)" }}>
                      {tm.name}{tm.role ? ` · ${tm.role}` : ""}
                      <span className="ml-2 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]"
                        style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}
                        data-testid={`testimonial-source-${tm.id}`}>
                        {tm.source === "user" ? "Seeker submitted" : "Admin added"}
                      </span>
                      {!tm.published && (
                        <span className="ml-2 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]"
                          style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                          Pending approval
                        </span>
                      )}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button data-testid={`testimonial-publish-${tm.id}`} onClick={() => togglePublish(tm)}
                        className="flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
                        style={tm.published
                          ? { borderColor: "var(--border-hover)", color: "var(--text-secondary)" }
                          : { borderColor: "var(--border-delicate)", backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                        {tm.published ? <><EyeOff size={11} /> Unpublish</> : <><Eye size={11} /> Publish</>}
                      </button>
                      <button data-testid={`testimonial-edit-${tm.id}`}
                        onClick={() => { setTEditing(tm.id); setTForm({ text: tm.text, name: tm.name, role: tm.role || "", published: tm.published }); window.scrollTo(0, 0); }}
                        className="flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
                        style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}>
                        <Pencil size={11} /> Edit
                      </button>
                      <button data-testid={`testimonial-delete-${tm.id}`} onClick={() => deleteTestimonial(tm.id)}
                        aria-label={`Delete testimonial by ${tm.name}`}
                        className="rounded-full border p-1.5"
                        style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                {testimonials.length === 0 && (
                  <p className="py-4 text-center text-xs" style={{ color: "var(--text-muted)" }}>No testimonials yet — add the first one above.</p>
                )}
              </div>
            </section>

            <section className="mb-14 rounded-3xl border p-6 sm:p-8" style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}>
              <h2 className="font-display flex items-center gap-2 text-xl sm:text-2xl">
                <CalendarPlus size={18} style={{ color: "var(--accent-gold)" }} /> Upcoming Events
              </h2>
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>These appear on the site's Events section instantly.</p>

              <form onSubmit={addEvent} className="mt-6 grid gap-4 sm:grid-cols-2">
                <input data-testid="event-input-title" required value={eventForm.title} onChange={setEv("title")}
                  className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Event title" />
                <select data-testid="event-select-type" value={eventForm.eventType} onChange={setEv("eventType")}
                  className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle}>
                  {EVENT_TYPES.map((ty) => <option key={ty}>{ty}</option>)}
                </select>
                <div>
                  <label className="mb-1 block font-mono-alt text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>Start date</label>
                  <input data-testid="event-input-date" required type="date" value={eventForm.date} onChange={setEv("date")}
                    className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} />
                </div>
                <div>
                  <label className="mb-1 block font-mono-alt text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>End date (optional)</label>
                  <input data-testid="event-input-enddate" type="date" value={eventForm.endDate} onChange={setEv("endDate")}
                    className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} />
                </div>
                <input data-testid="event-input-location" value={eventForm.location} onChange={setEv("location")}
                  className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Location (city or Online)" />
                <input data-testid="event-input-link" value={eventForm.link || ""} onChange={setEv("link")}
                  className="rounded-xl border px-4 py-2.5 text-sm outline-none" style={inputStyle} placeholder="Join/Register link (optional, https://…)" />
                <textarea data-testid="event-input-description" rows={2} value={eventForm.description} onChange={setEv("description")}
                  className="resize-none rounded-xl border px-4 py-2.5 text-sm outline-none sm:col-span-2" style={inputStyle} placeholder="Short description" />
                <button data-testid="event-add-button" type="submit" disabled={saving}
                  className="rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-60 sm:col-span-2"
                  style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}>
                  {saving ? "Adding…" : "Add Event"}
                </button>
              </form>

              <div data-testid="admin-events-list" className="mt-6 space-y-3">
                {events.map((ev) => (
                  <div key={ev.id} data-testid={`admin-event-${ev.id}`}
                    className="flex items-center justify-between gap-4 rounded-2xl border px-5 py-3.5"
                    style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-canvas)" }}>
                    <div>
                      <p className="text-sm font-medium">{ev.title}</p>
                      <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
                        {ev.date}{ev.endDate ? ` → ${ev.endDate}` : ""} · {ev.eventType}{ev.location ? ` · ${ev.location}` : ""}
                      </p>
                    </div>
                    <button data-testid={`event-delete-${ev.id}`} onClick={() => deleteEvent(ev.id)}
                      aria-label={`Delete ${ev.title}`}
                      className="rounded-full border p-2 transition-colors"
                      style={{ borderColor: "var(--border-delicate)", color: "var(--text-muted)" }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="py-4 text-center text-xs" style={{ color: "var(--text-muted)" }}>No events yet — add the first one above.</p>
                )}
              </div>
            </section>

            <div data-testid="admin-inquiries-list" className="space-y-4">
              {(inquiries || []).map((q) => (
                <article key={q.id} className="rounded-2xl border p-6" style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="font-display text-xl">{q.fullName}</h2>
                    <span className="font-mono-alt text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
                      {new Date(q.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-xs" style={{ color: "var(--accent-gold)" }}>
                    {q.inquiryType}{q.location ? ` · ${q.location}` : ""}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{q.email}</p>
                  {(q.phone || q.eventDate || q.address) && (
                    <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                      {[q.phone, q.eventDate, q.address].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{q.message}</p>
                  <div className="mt-4 border-t pt-4" style={{ borderColor: "var(--border-delicate)" }}>
                    <label className="mb-2 flex items-center gap-1.5 font-mono-alt text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
                      <StickyNote size={12} /> Private note
                    </label>
                    <div className="flex gap-2">
                      <input
                        data-testid={`note-input-${q.id}`}
                        value={notes[q.id] || ""}
                        onChange={(e) => setNotes((n) => ({ ...n, [q.id]: e.target.value }))}
                        className="flex-1 rounded-xl border px-4 py-2.5 text-sm outline-none"
                        style={inputStyle}
                        placeholder="e.g. Replied on WhatsApp, follow up in May…"
                      />
                      <button
                        data-testid={`note-save-${q.id}`}
                        onClick={() => saveNote(q.id)}
                        className="rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em]"
                        style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}
                      >
                        Save
                      </button>
                    </div>
                    {(q.noteLogs || []).length > 0 && (
                      <div data-testid={`note-logs-${q.id}`} className="mt-3 space-y-1.5">
                        {[...q.noteLogs].reverse().map((l, idx) => (
                          <p key={idx} className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                            <span className="font-mono-alt text-[10px]" style={{ color: "var(--accent-gold)" }}>
                              {new Date(l.savedAt).toLocaleString()}
                            </span>
                            {" — "}{l.note || "(note cleared)"}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {inquiries && inquiries.length === 0 && (
                <p className="py-16 text-center text-sm" style={{ color: "var(--text-muted)" }}>No inquiries yet — the inbox is silent.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
